import React, {
  FC,
  Key,
  memo,
  PropsWithChildren,
  ReactText,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Undefinable } from '@symfa-inc/providence-verizon-types';
import { Alert, Spin } from 'antd';
import { HttpService } from '@core/services';
import { PaceTaskHttpService } from '@core/services/http';
import { toggleModal } from '@core/utils/methods';
import { PaceTask, TabComponentProps } from '@models/interfaces';
import { DeleteModal, PaceTasks, Search } from '@shared/components';
import { PrimaryButton } from '@shared/modules';
import { AccessPaceTaskModal, AddAndEditPaceTaskModal } from '../../modals';
import { useAllPaceTasks } from './hooks/use-all-pace-tasks';
import { getAllPaceTaskColumns } from './models/constants';

import './styles.scss';

const AllPaceTasksComponent: FC<TabComponentProps> = ({
  isActive,
}: PropsWithChildren<TabComponentProps>) => {
  const paceTaskService = useMemo(
    () => HttpService.getHttpRequests(PaceTaskHttpService),
    [],
  );

  const {
    data: paceTaskData,
    isLoading,
    hasError,
    setSearch,
    invalidate,
  } = useAllPaceTasks(isActive);

  const [selectedRows, setSelectedRows] = useState<ReactText[]>([]);
  const [isAddModalVisible, setAddModalVisible] = useState<boolean>(false);
  const [isAccessModalVisible, setAccessModalVisible] =
    useState<boolean>(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<PaceTask>();
  const [editablePaceTask, setEditablePaceTask] =
    useState<Undefinable<PaceTask>>();

  useEffect(() => {
    if (selectedRows.length) {
      setSelectedRows(
        selectedRows.filter(
          (id: ReactText): boolean =>
            !!paceTaskData.find((p: PaceTask) => p.id === id),
        ),
      );
    }
  }, [paceTaskData]);

  const rowSelection = {
    selectedRowKeys: selectedRows,
    onChange: (rows: Key[]): void => setSelectedRows(rows as ReactText[]),
  };

  const toggleAddTaskModal = (): void => {
    setAddModalVisible(!isAddModalVisible);

    if (isAddModalVisible && editablePaceTask) {
      setEditablePaceTask(undefined);
    }
  };

  const toggleAccessModal = (): void =>
    setAccessModalVisible(!isAccessModalVisible);

  const editPaceTask = useCallback((paceTask: PaceTask): void => {
    setEditablePaceTask(paceTask);
    setAddModalVisible(true);
  }, []);

  const onDeletePaceTaskBtn = useCallback((paceTask: PaceTask): void => {
    setSelectedItem(paceTask);
    setDeleteModalVisible(true);
  }, []);

  const columns = useMemo(
    () => getAllPaceTaskColumns(editPaceTask, onDeletePaceTaskBtn),
    [editPaceTask, onDeletePaceTaskBtn],
  );

  const deletePaceTask = async (): Promise<void> => {
    if (selectedItem) {
      await paceTaskService.deletePaceTask(selectedItem.id);
      setSelectedItem(undefined);
    }

    setDeleteModalVisible(false);
  };

  if (hasError) {
    return (
      <div className="prov-all-pace-tasks prov-all-pace-tasks--error">
        <Alert type="error" message="Failed to load pace tasks" />
        <PrimaryButton title="Retry" onClick={invalidate} />
      </div>
    );
  }

  return (
    <Spin spinning={isLoading} size="large">
      <div className="prov-all-pace-tasks">
        <AddAndEditPaceTaskModal
          paceTask={editablePaceTask}
          visible={isAddModalVisible}
          onOk={toggleAddTaskModal}
          onCancel={toggleAddTaskModal}
        />
        <AccessPaceTaskModal
          paceTaskId={selectedRows as string[]}
          visible={isAccessModalVisible}
          onOk={toggleAccessModal}
          onCancel={toggleAccessModal}
        />
        <PaceTasks
          columns={columns}
          data={paceTaskData}
          infoRowLeftElement={
            <Search onChange={(value: string): void => setSearch(value)} />
          }
          infoRowRightElement={
            <PrimaryButton
              title="Add Task"
              icon="+"
              onClick={toggleAddTaskModal}
            />
          }
          rowSelection={rowSelection}
        />
        <DeleteModal
          onOk={deletePaceTask}
          onCancel={toggleModal(setDeleteModalVisible)}
          visible={deleteModalVisible}
        />
        <PrimaryButton
          disabled={!selectedRows.length}
          title="Assign Access"
          icon="+"
          onClick={toggleAccessModal}
          className="prov-all-pace-tasks__access-btn"
        />
      </div>
    </Spin>
  );
};

export const AllPaceTasks = memo(AllPaceTasksComponent);
